export type BusinessType = 'saas' | 'ecommerce' | 'services' | 'digital_products' | 'coaching' | 'media'
export type OnboardingPlaybookWizardId = 'subscriberSurvey' | 'smartCtaFunnel' | 'leadGenQuiz'
export type PersonalizedPlaybookCopy = { title: string; description: string; result: string }
export type RecommendedPlaybook = { key: OnboardingPlaybookWizardId; reason: string }
export type OnboardingAiContext = {
  businessName: string | null
  businessType: BusinessType | null
  playbookRecommendations: Record<OnboardingPlaybookWizardId, PersonalizedPlaybookCopy> | null
  recommendedPlaybook: RecommendedPlaybook | null
}
export type ActivationResearchAudience = {
  label: string
  description: string
  likelyGoals: string[]
  likelyPainPoints: string[]
  qualificationSignals: string[]
}
export type ActivationResearchOffer = {
  name: string
  type: 'demo' | 'trial' | 'download' | 'purchase' | 'consultation' | 'newsletter' | 'course' | 'other'
  url: string | null
  bestFor: string[]
  ctaLanguage: string | null
}
export type ActivationResearchContext = {
  wizardId: OnboardingPlaybookWizardId
  selectedRecommendation: PersonalizedPlaybookCopy
  recommendedFirstWin: 'subscriber_survey' | 'lead_gen_quiz' | 'smart_cta_funnel' | 'personalize_page' | null
  primaryAudiences: ActivationResearchAudience[]
  offerLadder: ActivationResearchOffer[]
  surveyLearningAreas: string[]
  routingSignals: string[]
  disqualificationSignals: string[]
  personalizationAngles: string[]
  evidenceConfidence: 'low' | 'medium' | 'high' | null
}

const supportedWizardIds: readonly OnboardingPlaybookWizardId[] = ['subscriberSurvey', 'smartCtaFunnel', 'leadGenQuiz']
const personalizedPlaybookFieldMaxLengths = {
  title: 80,
  description: 220,
  result: 180
} as const
const supportedOfferTypes = [
  'demo',
  'trial',
  'download',
  'purchase',
  'consultation',
  'newsletter',
  'course',
  'other'
] as const
const supportedRecommendedFirstWins = [
  'subscriber_survey',
  'lead_gen_quiz',
  'smart_cta_funnel',
  'personalize_page'
] as const
const supportedEvidenceConfidences = ['low', 'medium', 'high'] as const

const readNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export const normalizeBusinessType = (value: unknown): BusinessType | null => {
  switch (readNonEmptyString(value)?.toLowerCase()) {
    case 'saas':
      return 'saas'
    case 'ecommerce':
      return 'ecommerce'
    case 'services':
      return 'services'
    case 'digital_products':
      return 'digital_products'
    case 'coaching':
      return 'coaching'
    case 'media':
      return 'media'
    case undefined:
      break
  }

  return null
}

const readPlaybookField = (
  value: unknown,
  maxCharacters: (typeof personalizedPlaybookFieldMaxLengths)[keyof typeof personalizedPlaybookFieldMaxLengths]
) => {
  const text = readNonEmptyString(value)
  if (!text) {
    return null
  }

  return Array.from(text).length <= maxCharacters ? text : null
}

const parsePersonalizedPlaybookCopy = (value: unknown): PersonalizedPlaybookCopy | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const copy = value as Record<string, unknown>
  const keys = Object.keys(copy).sort()

  if (keys.length !== 3 || keys.join('|') !== 'description|result|title') {
    return null
  }

  const title = readPlaybookField(copy.title, personalizedPlaybookFieldMaxLengths.title)
  const description = readPlaybookField(copy.description, personalizedPlaybookFieldMaxLengths.description)
  const result = readPlaybookField(copy.result, personalizedPlaybookFieldMaxLengths.result)

  if (!title || !description || !result) {
    return null
  }

  return { title, description, result }
}

const parsePersonalizedPlaybookRecommendations = (
  value: unknown
): Record<OnboardingPlaybookWizardId, PersonalizedPlaybookCopy> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const recommendations = value as Record<string, unknown>
  const keys = Object.keys(recommendations).sort()
  const expectedKeys = [...supportedWizardIds].sort()

  if (keys.length !== expectedKeys.length || keys.join('|') !== expectedKeys.join('|')) {
    return null
  }

  const subscriberSurvey = parsePersonalizedPlaybookCopy(recommendations.subscriberSurvey)
  const smartCtaFunnel = parsePersonalizedPlaybookCopy(recommendations.smartCtaFunnel)
  const leadGenQuiz = parsePersonalizedPlaybookCopy(recommendations.leadGenQuiz)

  if (!subscriberSurvey || !smartCtaFunnel || !leadGenQuiz) {
    return null
  }

  return {
    subscriberSurvey,
    smartCtaFunnel,
    leadGenQuiz
  }
}

const readStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map((entry) => readNonEmptyString(entry)).filter((entry): entry is string => entry !== null)
}

const readPrimaryAudience = (value: unknown): ActivationResearchAudience | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const audience = value as Record<string, unknown>
  const label = readNonEmptyString(audience.label)
  const description = readNonEmptyString(audience.description)

  if (!label || !description) {
    return null
  }

  return {
    label,
    description,
    likelyGoals: readStringArray(audience.likelyGoals),
    likelyPainPoints: readStringArray(audience.likelyPainPoints),
    qualificationSignals: readStringArray(audience.qualificationSignals)
  }
}

const readOfferType = (value: unknown): ActivationResearchOffer['type'] | null => {
  const type = readNonEmptyString(value)
  if (!type) {
    return null
  }

  return supportedOfferTypes.indexOf(type as ActivationResearchOffer['type']) >= 0
    ? (type as ActivationResearchOffer['type'])
    : null
}

const readOffer = (value: unknown): ActivationResearchOffer | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const offer = value as Record<string, unknown>
  const name = readNonEmptyString(offer.name)
  const type = readOfferType(offer.type)

  if (!name || !type) {
    return null
  }

  return {
    name,
    type,
    url: readNonEmptyString(offer.url),
    bestFor: readStringArray(offer.bestFor),
    ctaLanguage: readNonEmptyString(offer.ctaLanguage)
  }
}

const readRecommendedFirstWin = (value: unknown): ActivationResearchContext['recommendedFirstWin'] => {
  const firstWin = readNonEmptyString(value)
  if (!firstWin) {
    return null
  }

  return supportedRecommendedFirstWins.indexOf(
    firstWin as NonNullable<ActivationResearchContext['recommendedFirstWin']>
  ) >= 0
    ? (firstWin as NonNullable<ActivationResearchContext['recommendedFirstWin']>)
    : null
}

const readEvidenceConfidence = (value: unknown): ActivationResearchContext['evidenceConfidence'] => {
  const confidence = readNonEmptyString(value)
  if (!confidence) {
    return null
  }

  return supportedEvidenceConfidences.indexOf(
    confidence as NonNullable<ActivationResearchContext['evidenceConfidence']>
  ) >= 0
    ? (confidence as NonNullable<ActivationResearchContext['evidenceConfidence']>)
    : null
}

const isSupportedWizardId = (value: string | null | undefined): value is OnboardingPlaybookWizardId =>
  !!value && supportedWizardIds.indexOf(value as OnboardingPlaybookWizardId) >= 0

export const parseOnboardingAiContext = (value: unknown): OnboardingAiContext => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { businessName: null, businessType: null, playbookRecommendations: null, recommendedPlaybook: null }
  }

  const context = value as Record<string, unknown>
  const activation =
    context.activation && typeof context.activation === 'object' && !Array.isArray(context.activation)
      ? (context.activation as Record<string, unknown>)
      : null
  const playbookRecommendations = parsePersonalizedPlaybookRecommendations(activation?.playbookRecommendations)
  const recommendation = activation?.recommendedPlaybook
  let recommendedPlaybook: RecommendedPlaybook | null = null
  if (
    playbookRecommendations &&
    recommendation &&
    typeof recommendation === 'object' &&
    !Array.isArray(recommendation)
  ) {
    const candidate = recommendation as Record<string, unknown>
    const key = readNonEmptyString(candidate.key)
    const reason = readNonEmptyString(candidate.reason)
    if (isSupportedWizardId(key) && playbookRecommendations[key] && reason) {
      recommendedPlaybook = { key, reason }
    }
  }

  return {
    businessName: readNonEmptyString(context.businessName),
    businessType: normalizeBusinessType(context.businessType),
    playbookRecommendations,
    recommendedPlaybook
  }
}

export const buildActivationResearchContext = (
  value: unknown,
  wizardId: string | null | undefined
): ActivationResearchContext | null => {
  if (!isSupportedWizardId(wizardId)) {
    return null
  }

  const parsed = parseOnboardingAiContext(value)
  if (!parsed.playbookRecommendations) {
    return null
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const context = value as Record<string, unknown>
  const activation =
    context.activation && typeof context.activation === 'object' && !Array.isArray(context.activation)
      ? (context.activation as Record<string, unknown>)
      : null
  const evidence =
    context.evidence && typeof context.evidence === 'object' && !Array.isArray(context.evidence)
      ? (context.evidence as Record<string, unknown>)
      : null

  return {
    wizardId,
    selectedRecommendation: parsed.playbookRecommendations[wizardId],
    recommendedFirstWin: readRecommendedFirstWin(activation?.recommendedFirstWin),
    primaryAudiences: Array.isArray(activation?.primaryAudiences)
      ? activation.primaryAudiences
          .map(readPrimaryAudience)
          .filter((entry): entry is ActivationResearchAudience => entry !== null)
      : [],
    offerLadder: Array.isArray(activation?.offerLadder)
      ? activation.offerLadder.map(readOffer).filter((entry): entry is ActivationResearchOffer => entry !== null)
      : [],
    surveyLearningAreas: readStringArray(activation?.surveyLearningAreas),
    routingSignals: readStringArray(activation?.routingSignals),
    disqualificationSignals: readStringArray(activation?.disqualificationSignals),
    personalizationAngles: readStringArray(activation?.personalizationAngles),
    evidenceConfidence: readEvidenceConfidence(evidence?.confidence)
  }
}
